export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? "/api/v1"}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...init.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as {
    detail?: string;
    message?: string;
    title?: string;
    correlationId?: string;
  };
  if (!response.ok)
    throw new ApiError(
      response.status,
      payload.detail ?? payload.message ?? "The request could not be completed.",
      payload.title,
      payload.correlationId,
    );
  return payload as T;
}

/** Renews the sign-in a request was refused with; null when it can't, or it's now someone else's. */
type SessionRenewal = (refusedIdToken: string) => Promise<string | null>;
let renewSignedInSession: SessionRenewal | undefined;

/** The signed-in session's renewal (set by AuthSession), so an expired sign-in can be renewed. */
export function setSessionRenewal(renewal: SessionRenewal | undefined) {
  renewSignedInSession = renewal;
}

export async function apiRequest<T>(
  path: string,
  idToken: string,
  init: RequestInit = {},
): Promise<T> {
  const send = (token: string) =>
    request<T>(path, {
      ...init,
      headers: { authorization: `Bearer ${token}`, ...init.headers },
    });
  try {
    return await send(idToken);
  } catch (error) {
    // API Gateway refuses an expired sign-in (401 with no body) before any service runs, so the
    // request was never processed: renew the sign-in once and send the same request again. A
    // background tab can wake past its renewal (2026-11-01 voucher incident).
    if (!(error instanceof ApiError) || error.status !== 401 || error.code) throw error;
    const fresh = await renewSignedInSession?.(idToken).catch(() => null);
    if (!fresh || fresh === idToken) throw error;
    return send(fresh);
  }
}

export async function publicApiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  return request<T>(path, init);
}
